import { authService } from './authService'
import { API_BASE_URL } from '../config/api'

class ApiService {
  constructor() {
    this.isRefreshing = false
    this.failedQueue = []
  }

  // Process failed requests queue
  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve(token)
      }
    })
    this.failedQueue = []
  }

  // Get auth headers
  getAuthHeaders() {
    const token = authService.getAccessToken()
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  // Make API request with automatic token refresh
  async request(url, options = {}) {
    // Add auth headers
    const headers = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
      ...options.headers,
    }

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers,
      })

      // If unauthorized, try to refresh token
      if (response.status === 401 && !this.isRefreshing) {
        if (!authService.getRefreshToken()) {
          authService.clearAuth()
          authService.notifySessionExpired()
          return response
        }
        this.isRefreshing = true

        try {
          // Try to refresh the token
          await authService.refreshToken()

          // Retry the original request with new token
          const newHeaders = {
            ...headers,
            ...this.getAuthHeaders(),
          }

          const retryResponse = await fetch(`${API_BASE_URL}${url}`, {
            ...options,
            headers: newHeaders,
          })

          this.isRefreshing = false
          this.processQueue(null, authService.getAccessToken())

          return retryResponse
        } catch (refreshError) {
          this.isRefreshing = false
          this.processQueue(refreshError, null)

          // Clear auth and let the auth wrapper show the login prompt without
          // reloading into the same failing request loop.
          authService.clearAuth()
          authService.notifySessionExpired()
          throw refreshError
        }
      }

      return response
    } catch (error) {
      if (this.isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject })
        })
          .then(() => {
            return this.request(url, options)
          })
          .catch((error) => {
            throw error
          })
      }
      throw error
    }
  }

  // GET request
  async get(url) {
    const response = await this.request(url, { method: 'GET' })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  }

  // POST request
  async post(url, data) {
    const response = await this.request(url, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  }

  // PUT request
  async put(url, data) {
    const response = await this.request(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  }

  // DELETE request
  async delete(url) {
    const response = await this.request(url, { method: 'DELETE' })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  }
}

export const apiService = new ApiService()
